/**
 * BaseGameLoop - drives the frame update cycle and manages the active World.
 * Handles requestAnimationFrame, delta time calculation, and world lifecycle.
 */
import * as THREE from 'three';

import { Actor } from '../actors/Actor.js';
import { Pawn } from '../actors/Pawn.js';
import { PlayerController } from '../actors/PlayerController.js';
import { SceneComponent } from '../components/SceneComponent.js';
import { ManifoldWrapper } from '../csg/ManifoldWrapper.js';
import { tryApplyLightmapsAfterSceneReady } from '../lightmapper/applySceneLightmaps.js';
import { ClientNetWorld } from '../multiplayer/ClientNetWorld.js';
import { ServerGameSession } from '../multiplayer/implementations/ServerGameSession.js';
import { NetRuntime, NetRuntimeType } from '../multiplayer/NetRuntime.js';
import { NetWorld } from '../multiplayer/NetWorld.js';
import { ServerAuthorityNetWorld } from '../multiplayer/ServerAuthorityNetWorld.js';
import { StandaloneNetWorld } from '../multiplayer/StandaloneNetWorld.js';
import { createNavigationServer, NavigationEngine } from '../navigation/NavigationServer.js';
import { RecastNavigationServer } from '../navigation/RecastNavigationServer.js';
import { createPhysicsEngine } from '../physics/createPhysicsEngine.js';
import { PhysicsEngine } from '../physics/PhysicsEngine.js';
import { GenesysScene } from '../render/GenesysScene.js';
import { PostProcessManager } from '../render/PostProcessManager.js';
import { Renderer, type RendererOptions } from '../render/Renderer.js';
import { projectInfo } from '../storage/projectContext.js';
import { AssetPath } from '../storage/storageProvider.js';
import { type Constructor } from '../systems/ClassRegistry.js';
import { ConsoleCommand } from '../systems/ConsoleManager.js';
import { ObjectTracker } from '../systems/ObjectTracker.js';
import {
  collectPerformanceSnapshot,
  downloadPerformanceSnapshot,
  formatPerformanceSnapshotSummary,
} from '../systems/PerformanceSnapshot.js';
import { Stats } from '../systems/Stats.js';
import { WorldSerializer } from '../systems/WorldSerialization.js';
import { ConnectionStatusOverlay } from '../ui/ConnectionStatusOverlay.js';
import { Delegate } from '../utils/Delegate.js';
import { ScenePath } from '../utils/index.js';
import { resourceManager, ResourceType } from '../utils/ResourceManager.js';
import { spawn } from '../utils/Spawn.js';
import { isNode, isPublishedGame, parseJson } from '../utils/Utils.js';
import { createVRButton } from '../utils/VRButtonUtils.js';

import { GameLoopDebugUI } from './dev/GameLoopDebugUI.js';
import { GameMode, type GameModeOptions } from './GameMode.js';
import { Level } from './Level.js';
import { PlayState } from './Playable.js';
import {
  FadeToBlackTransition,
  type ISceneTransition,
  type TransitionContext,
} from './SceneTransition.js';
import { World } from './World.js';

import type { InspectorToggleOffset, InspectorTogglePosition } from './dev/debugInspectorOptions.js';
import type { DebugUIMode } from './dev/GameLoopDebugUI.js';
import type { RenderWindowSize, WorldOptions } from './World.js';
import type { NetSettings } from '../multiplayer/NetWorld.js';
import type { INavigationServer } from '../navigation/NavigationServer.js';
import type { IPhysicsEngine } from '../physics/PhysicsEngine.js';
import type { IGenesysRenderer } from '../render/Renderer.js';

export type { DebugUIMode } from './dev/GameLoopDebugUI.js';
export type { InspectorTogglePosition, InspectorToggleOffset } from './dev/debugInspectorOptions.js';

/** Options for {@link BaseGameLoop.openLevel}. */
export interface OpenLevelOptions {
  /**
   * When `false`, the current world is fully torn down with no actors carried over
   * (e.g. {@link BaseGameLoop.disconnectFromServer}). Defaults to `true`.
   */
  preserveActors?: boolean;
}

export interface IGameLoop {
  loadLevelData(levelData: any): Promise<Level>;
  loadLevelDataFromPath(path: AssetPath): Promise<Level>;

  start(): Promise<void>;
  destroy(): void;
  setInputEnabled(enabled: boolean): void;
  getWorld(): World | null;
  getLevel(): Level | null;
  get rootScene(): THREE.Scene | null;
}

export interface BaseGameLoopOptions {
  backgroundColor: THREE.ColorRepresentation;
  gravity: THREE.Vector3;
  gameId?: string;
  xrEnabled?: boolean;
  /** WebXR session options for VR button */
  vrSessionOptions?: {
    optionalFeatures?: string[];
    requiredFeatures?: string[];
    framebufferScaleFactor?: number;
  };
  /** Renderer options (supports both WebGL and WebGPU) */
  rendererOptions?: RendererOptions;

  /** Initial world/scene path to load on startup. If omitted, creates an empty world. */
  initialWorldPath?: AssetPath;

  /** Default scene path to reload when disconnecting from a multiplayer server. */
  defaultScenePath?: AssetPath;

  /** Networking settings for multiplayer client/server runtime. */
  netSettings?: NetSettings;

  /** Default GameMode class to instantiate when the scene doesn't specify one. */
  defaultGameModeClass?: Constructor<GameMode>;

  /** Options passed to the default GameMode initialize method. */
  defaultGameModeOptions?: GameModeOptions;

  /**
   * Force headless mode (no rendering/UI) even in non-server environments.
   * Useful for testing or running browser-based simulations without rendering.
   * Defaults to true when NetRuntime.isDedicatedServer() is true or running in Node.js.
   */
  headless?: boolean;

  /**
   * Debug HUD: `none`, legacy `stats.js` FPS plus engine draw-call overlay, or Three.js addon inspector.
   * @defaultValue `'inspector'`
   */
  debugUIMode?: DebugUIMode;

  /** Corner or edge anchor for the Three.js inspector FPS toggle (`#profiler-toggle`) inside the game container. */
  inspectorTogglePosition?: InspectorTogglePosition;
  /** Pixel inset from the anchored edge; how `x`/`y` apply depends on {@link InspectorTogglePosition} (see {@link InspectorToggleOffset}). */
  inspectorToggleOffset?: InspectorToggleOffset;
  /** Uniform CSS scale for the FPS toggle (invalid or non-positive values fall back in layout code). */
  inspectorToggleScale?: number;
}

export interface TickTime {
  currentTimeMS: number;
  deltaTimeMS: number;
}

export class BaseGameLoop implements IGameLoop {
  private _rootScene: GenesysScene;
  /** Camera used to render the root scene when no world is active during a level transition. */
  private readonly _transitionFallbackCamera: THREE.PerspectiveCamera;
  protected _world: World | null = null;
  private _levelTransitionInProgress = false;
  private _activeSceneTransition: { transition: ISceneTransition; ctx: TransitionContext } | null = null;
  private _transitionFrameWaiter: { predicate: () => boolean; resolve: () => void } | null = null;
  private _debugUI: GameLoopDebugUI | null = null;
  protected lastTickTimeMS: number = 0;
  private _renderer: Renderer | null = null;
  protected destroyed: boolean = false;
  protected options: BaseGameLoopOptions;
  public readonly onWorldDataLoaded: Delegate<[Level], void> = new Delegate<[Level], void>();
  private vrButton: HTMLElement | null = null;
  /** Current level loaded into the active world (separate from the world itself). */
  protected currentLevel: Level | null = null;

  /** Scene file used for co-located resources (e.g. build-data zip). */
  protected _activeScenePath: AssetPath | null = null;

  /** Network world for multiplayer - persists across world transitions */
  protected netWorld: NetWorld;

  /** Connection status HUD — session-scoped, survives world reloads */
  private connectionStatusOverlay: ConnectionStatusOverlay | null = null;

  /** Manifold WASM wrapper — session-scoped, persists across world transitions. */
  private _manifold: ManifoldWrapper | null = null;

  /** Physics engine — session-scoped; simulation state resets on level transitions. */
  private _physicsEngine: IPhysicsEngine | null = null;

  /** Navigation server — session-scoped; navmesh clears on level transitions. */
  private _navigationServer: INavigationServer | null = null;

  /** Post-process manager — session-scoped; per-level state resets on world transitions. */
  private _postProcessManager: PostProcessManager | null = null;

  static get DEFAULT_OPTIONS(): BaseGameLoopOptions {
    return {
      backgroundColor: 0x87CEEB,
      gravity: new THREE.Vector3(0, -9.81, 0),
      xrEnabled: false,
      debugUIMode: 'auto',
      inspectorTogglePosition: 'top-left',
      inspectorToggleOffset: { x: 10, y: 10 },
      inspectorToggleScale: 0.8,
    };
  }

  protected readonly headless: boolean = false;

  get renderer(): Renderer | null {
    return this._renderer;
  }
  protected set renderer(value: Renderer | null) {
    this._renderer = value;
  }

  get debugUI(): GameLoopDebugUI | null {
    return this._debugUI;
  }

  //====================================================================================================

  constructor(protected container: HTMLElement, options: Partial<BaseGameLoopOptions> = {}) {
    ObjectTracker.track(this);
    // clear the cached resources to avoid loading stale resources
    resourceManager.clearCachedResources();

    this._rootScene = new GenesysScene();
    this._transitionFallbackCamera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);

    // Support nested options format where gameId may contain additional options.
    if (options.gameId && typeof options.gameId === 'object') {
      // Extract the nested options and merge with top-level options
      const nestedOptions = options.gameId as any;
      options = {
        ...options,
        ...nestedOptions,
        gameId: nestedOptions.gameId, // Extract the actual gameId string
      };
    }

    // Merge provided options with defaults.
    const defaults = BaseGameLoop.DEFAULT_OPTIONS;
    this.options = {
      ...defaults,
      ...options,
      inspectorToggleOffset: {
        x: options.inspectorToggleOffset?.x ?? defaults.inspectorToggleOffset?.x ?? 10,
        y: options.inspectorToggleOffset?.y ?? defaults.inspectorToggleOffset?.y ?? 10,
      },
    };

    // Create NetWorld early — it persists across world transitions.
    // Dedicated servers use ServerAuthorityNetWorld; all other modes start as StandaloneNetWorld.
    // connectToServer() swaps StandaloneNetWorld → ClientNetWorld on connect and back on disconnect.
    const gameId = this.options.gameId ?? 'default';
    this.netWorld = NetRuntime.isDedicatedServer()
      ? new ServerAuthorityNetWorld(gameId)
      : new StandaloneNetWorld(gameId);

    // Skip renderer in headless mode
    this.headless = options.headless ?? (NetRuntime.isDedicatedServer() || isNode());
    if (!this.headless) {
      this.container.style.position = 'relative';
    }

  }
  get rootScene(): GenesysScene {
    return this._rootScene;
  }

  /**
   * Destroys the active world while keeping session subsystems and the external renderer alive.
   */
  public async unloadCurrentLevel(): Promise<void> {
    await this.destroyCurrentWorld();
  }

  /**
   * Brings the current world into edit mode: session subsystems without {@link World.beginPlay}.
   */
  protected async enterEditMode(): Promise<void> {
    if (!this._world) {
      throw new Error('[GameLoop] Cannot enter edit mode — no world loaded');
    }

    await this.ensurePhysicsEngine(this._world);
    await this.ensureNavigationServer(this._world);
    await this.createManifold();
    this.ensurePostProcessManager();
    this.bindPostProcessManagerToWorld(this._world);
  }

  /**
   * Creates an empty world without level data. Caller must destroy any existing world first.
   */
  public async createEmptyLevelWorld(worldOptions?: Partial<WorldOptions>): Promise<World> {
    this._world = await this.createWorld({
      ...this.getWorldConfiguration(),
      gameId: this.options.gameId,
      gameLoop: new WeakRef(this),
      ...worldOptions,
    } as WorldOptions);
    this.currentLevel = null;
    return this._world;
  }

  /**
   * Sets up XR and console after a world has been created.
   * Called after world creation in the unified GameContext startup path.
   */
  protected setupWorldAfterCreation(): void {
    if (!this._world) return;

    // Set up XR if enabled in options
    if (this.options.xrEnabled && this.renderer) {
      this.renderer.enableXR();

      // Set framebuffer scale factor if specified
      if (this.options.vrSessionOptions?.framebufferScaleFactor !== undefined) {
        this.renderer.native.xr.setFramebufferScaleFactor(this.options.vrSessionOptions.framebufferScaleFactor);
        console.log('🎯 XR framebuffer scale factor set to:', this.options.vrSessionOptions.framebufferScaleFactor);
      }
    }

    this._world.consoleManager.addCommandHandler(this);
  }

  /**
   * Gets the current world. Throws if no world exists.
   */
  public get world(): World {
    if (!this._world) {
      throw new Error('[GameLoop] World not initialized. Call start() first when using GameContext.');
    }
    return this._world;
  }

  public get activeScenePath(): AssetPath | null {
    return this._activeScenePath;
  }

  public set activeScenePath(path: AssetPath | null) {
    this._activeScenePath = path;
  }

  /** The session Manifold wrapper, or `null` before {@link createManifold} completes. */
  public get manifold(): ManifoldWrapper | null {
    return this._manifold;
  }

  /**
   * Initializes the Manifold WASM module for CSG boolean operations.
   * Safe to call once per session. Skipped in headless mode (e.g. dedicated server).
   */
  public async createManifold(): Promise<void> {
    if (this._manifold || this.headless) {
      return;
    }

    this._manifold = new ManifoldWrapper();
    await this._manifold.initManifold();
  }

  private disposeManifold(): void {
    if (this._manifold) {
      this._manifold.dispose();
      this._manifold = null;
    }
  }

  public get physicsEngine(): IPhysicsEngine | null {
    return this._physicsEngine;
  }

  public get navigationServer(): INavigationServer | null {
    return this._navigationServer;
  }

  /**
   * Ensures the session physics engine exists and is bound to {@link world}.
   * Resets simulation state when reusing across level transitions.
   */
  public async ensurePhysicsEngine(world: World): Promise<void> {
    const options = world.options.physicsOptions;

    if (!this._physicsEngine) {
      this._physicsEngine = await createPhysicsEngine(options, world);
      console.log('[GameLoop] Physics engine created:', this._physicsEngine?.constructor.name);
      return;
    }

    await this._physicsEngine.resetForLevelTransition(world, options);
    console.log('[GameLoop] Physics engine reset for level transition');
  }

  /**
   * Ensures the session navigation server exists. Navmesh data is level-specific and cleared on transition.
   */
  public async ensureNavigationServer(world: World): Promise<void> {
    const options = world.options.navigationOptions;

    if (!this._navigationServer) {
      this._navigationServer = await createNavigationServer(options);
      console.log('[GameLoop] Navigation server created:', this._navigationServer?.constructor.name);
      return;
    }

    this._navigationServer?.clearNavigationMesh();
    this._navigationServer?.updateOptions?.(options.options ?? {});
  }

  protected cleanupSubsystemsForLevelTransition(): void {
    this._navigationServer?.clearNavigationMesh();
    this.resetPostProcessManagerForLevelTransition();
  }

  private disposePhysicsEngine(): void {
    if (this._physicsEngine) {
      this._physicsEngine.destroy();
      this._physicsEngine = null;
    }
  }

  private disposeNavigationServer(): void {
    if (this._navigationServer) {
      this._navigationServer.destroy();
      this._navigationServer = null;
    }
  }

  public get postProcessManager(): PostProcessManager | null {
    return this._postProcessManager;
  }

  /** Ensures the session post-process manager exists (created once per play session). */
  public ensurePostProcessManager(): void {
    if (this._postProcessManager) {
      return;
    }

    this._postProcessManager = new PostProcessManager();
    console.log('[GameLoop] PostProcessManager created');
  }

  /** Binds the session post-process manager to {@link world} (CSM hook + level configuration). */
  public bindPostProcessManagerToWorld(world: World): void {
    const manager = this._postProcessManager;
    if (!manager) {
      throw new Error('[GameLoop] PostProcessManager not created — call ensurePostProcessManager() first');
    }
    world.bindPostProcessManager(manager);
  }

  protected resetPostProcessManagerForLevelTransition(): void {
    this._postProcessManager?.resetForLevelTransition();
  }

  private disposePostProcessManager(): void {
    if (this._postProcessManager) {
      this._postProcessManager.destroy();
      this._postProcessManager = null;
    }
  }

  //====================================================================================================
  // World lifecycle (formerly GameContext)
  //====================================================================================================

  /**
   * One-time startup sequence. Dispatches to client/server/standalone mode and creates the first world.
   */
  private async startWorldLifecycle(): Promise<void> {
    console.log('[GameLoop] Starting world lifecycle...');
    const worldOptions: WorldOptions = {
      ...this.getWorldConfiguration(),
      gameId: this.options.gameId,
      gameLoop: new WeakRef(this),
    };

    const serverUrl = this.options.netSettings?.serverUrl;
    if (serverUrl) {
      worldOptions.gameMode = undefined;
      this._world = await this.createWorld(worldOptions);
      await this.connectToServer(serverUrl);
    } else if (this.options.initialWorldPath) {
      await this.startWithInitialLevel();
    } else {
      await this.startEmptyWorld(worldOptions);
    }
  }

  private async startWithInitialLevel(): Promise<void> {
    await this.coldLoadLevelFromPath(this.options.initialWorldPath!);

    if (NetRuntime.isDedicatedServer() && this.options.netSettings?.serverPortRange) {
      await this.startDedicatedServer();
    }
  }

  private async startEmptyWorld(worldOptions: WorldOptions): Promise<void> {
    console.log('[GameLoop] Creating empty world');
    worldOptions.gameMode ??= this.createDefaultGameMode();
    this._world = await this.createWorld(worldOptions);
  }

  private async startDedicatedServer(): Promise<void> {
    const port = await this._world!.netWorld.startServer(this.options.netSettings!.serverPortRange!, {
      lagSimulationConfig: this.options.netSettings?.serverLagSimulationConfig,
    });
    console.log(`[GameLoop] ✓ Server listening on port ${port}`);
    console.log(`[GameLoop] ✓ Clients can connect to: ws://localhost:${port}`);

    // Signal that the initial scene is ready so clients receive the correct @project scene path.
    const scenePath = (this.activeScenePath ?? this.options.initialWorldPath!).asStringPath;
    this._world!.netWorld.setScenePath(scenePath);
    this._world!.netWorld.onServerMapReady();
  }

  /**
   * Creates a new World instance. Override to customize world creation.
   */
  protected async createWorld(options: WorldOptions): Promise<World> {
    console.log('[GameLoop] Creating world...');
    if (!options.netWorld) {
      console.warn('[GameLoop] No netWorld provided in options - this may cause replication issues');
    }
    const world = new World(options);
    await this.ensurePhysicsEngine(world);
    this.ensurePostProcessManager();
    this.attachWorldScene(world);
    return world;
  }

  /** Parents the world's scene under the game loop root scene so actors render with the root graph. */
  private attachWorldScene(world: World): void {
    this._rootScene.add(world.scene);
  }

  private detachWorldScene(world: World): void {
    world.scene.removeFromParent();
  }

  /** Scene path used for {@link reloadInitialLevel} and post-disconnect reload. */
  protected getConfiguredInitialScenePath(): AssetPath | null {
    return this.options.defaultScenePath ?? this.options.initialWorldPath ?? null;
  }

  /**
   * Reloads the configured initial scene via {@link openLevel} (includes scene transition when not headless).
   */
  public async reloadInitialLevel(options: OpenLevelOptions = {}): Promise<void> {
    const scenePath = this.getConfiguredInitialScenePath();
    if (!scenePath) {
      console.warn('[GameLoop] No scene path configured for reload, cannot reload initial level');
      return;
    }
    await this.openLevel(scenePath.asStringPath, options);
  }

  /**
   * Cold level load: destroys any existing world, creates a fresh world with the scene's
   * GameMode, and deserializes level actors. Does not call beginPlay — use {@link enterPlayMode} next.
   */
  private async coldLoadLevelFromPath(scenePath: AssetPath): Promise<Level> {
    console.log(`[GameLoop] Cold loading level from ${scenePath}`);
    if (this._world) {
      await this.destroyCurrentWorld();
    }
    return this.loadLevelFromPath(scenePath);
  }

  /**
   * Creates a world and deserializes a scene without destroying an existing world.
   * Caller must destroy the current world first when replacing a level.
   */
  private async loadLevelFromPath(scenePath: AssetPath): Promise<Level> {
    const sceneData = await resourceManager.loadJsonObject(scenePath);
    if (!sceneData) {
      throw new Error(`[GameLoop] Failed to load scene data from ${scenePath}`);
    }

    const gameMode = this.resolveStartupGameMode(sceneData);
    this._world = await this.createWorld({
      ...this.getWorldConfiguration(),
      gameId: this.options.gameId,
      gameMode,
      gameLoop: new WeakRef(this),
    });

    this.currentLevel = await WorldSerializer.loadWorld(this._world, sceneData);
    this.onLevelLoaded(this.currentLevel);
    this.activeScenePath = scenePath;

    if (this._world.netWorld && NetRuntime.isDedicatedServer()) {
      this._world.netWorld.setScenePath(scenePath.asStringPath);
    }

    console.log(`[GameLoop] ✓ Level loaded from ${scenePath.asStringPath}`);
    return this.currentLevel;
  }

  private waitUntilNextFrames(predicate: () => boolean): Promise<void> {
    if (predicate()) {
      return Promise.resolve();
    }
    return new Promise(resolve => {
      this._transitionFrameWaiter = { predicate, resolve };
    });
  }

  private resolveTransitionFrameWaiter(): void {
    if (this._transitionFrameWaiter?.predicate()) {
      const { resolve } = this._transitionFrameWaiter;
      this._transitionFrameWaiter = null;
      resolve();
    }
  }

  /**
   * Brings the current world into play: subsystem setup, beginPlay, and local player spawn.
   * Shared by initial startup ({@link start}) and level transitions ({@link openLevel}).
   */
  protected async enterPlayMode(options: { runPreStart?: boolean } = {}): Promise<void> {
    if (!this._world) {
      throw new Error('[GameLoop] Cannot enter play mode — no world loaded');
    }

    this.setupWorldAfterCreation();
    await resourceManager.waitForResources(ResourceType.All);
    await this.loadSceneResources(this._world);
    await this.loadSceneLightmaps(this._world);

    if (options.runPreStart) {
      console.log('[GameLoop] calling preStart...');
      await this.preStart();
    }

    await this.ensureNavigationServer(this._world);
    await this.createManifold();
    this.bindPostProcessManagerToWorld(this._world);

    if (this._world.options.navigationOptions.generateOnStartUp) {
      await this._world.createNavigationMeshFromScene();
    }

    console.log('[GameLoop] calling world.beginPlay...');
    this._world.beginPlay();
    await this.netWorld.waitForLocalPlayerController();
  }

  protected async destroyCurrentWorld(): Promise<void> {
    if (!this._world) return;
    console.log('[GameLoop] Destroying current world...');
    if (this._world.isPlaying()) {
      this._world.endPlay();
    }
    this.cleanupSubsystemsForLevelTransition();
    this.currentLevel = null;
    this.detachWorldScene(this._world);
    this._world.destroy();
    this._world = null;
  }

  protected resolveStartupGameMode(sceneData: any): GameMode {
    const sceneGameMode = WorldSerializer.extractGameMode(sceneData);
    if (sceneGameMode) {
      console.log('[GameLoop] Using gameMode from scene:', sceneGameMode.constructor.name);
      return sceneGameMode;
    }
    return this.createDefaultGameMode();
  }

  protected createDefaultGameMode(): GameMode {
    const GameModeClass = this.options.defaultGameModeClass ?? GameMode;
    const gameMode = spawn(GameModeClass, this.options.defaultGameModeOptions);
    console.log('[GameLoop] Using default gameMode:', gameMode.constructor.name);
    return gameMode;
  }

  /**
   * Called when a level finishes loading. Override to react to level loads.
   */
  protected onLevelLoaded(level: Level): void {
    console.log(`[GameLoop] Level loaded with ${level.actors.length} actors`);
  }

  /**
   * Normalizes a map name or scene path for {@link openLevel} / {@link loadMap}.
   */
  protected resolveMapArgToScenePath(mapPath: string): ScenePath {
    if (mapPath.includes('.genesys-scene') || mapPath.startsWith('@')) {
      return mapPath;
    }
    return `@project/${mapPath}.genesys-scene`;
  }

  /**
   * Post-play hooks after a level is opened: server map-ready signal and {@link onWorldDataLoaded}.
   */
  protected async finalizeLevelOpen(level: Level): Promise<void> {
    if (NetRuntime.isDedicatedServer()) {
      this.netWorld.onServerMapReady();
    }
    this.onWorldDataLoaded.invoke(level);
  }

  //====================================================================================================

  /**
   * Get the world configuration. Override this to change the world configuration in your child class.
   * @returns The world configuration.
   */
  public getWorldConfiguration(): WorldOptions {
    const baseOptions = {
      backgroundColor: this.options.backgroundColor,
      physicsOptions: {
        gravity: this.options.gravity,
        engine: PhysicsEngine.Rapier
      },
      navigationOptions: {
        engine: NavigationEngine.RecastNavigation,
        debug: false,
        options: RecastNavigationServer.DEFAULT_OPTIONS,
        generateOnStartUp: false
      },
      enableGamepad: true,
      netWorld: this.netWorld
    };
    const isHeadless = this.options.headless ?? (NetRuntime.isDedicatedServer() || isNode());
    if (isHeadless) {
      return {
        ...baseOptions,
        headless: true
      };
    }
    const rendererDomElement = this.renderer?.native.domElement;
    if (!rendererDomElement) {
      throw new Error('[GameLoop] rendererDomElement is required for non-headless world configuration');
    }
    return {
      ...baseOptions,
      headless: false,
      rendererDomElement,
      gameContainer: this.container,
    };
  }

  public getDefaultRendererOptions(): RendererOptions {
    return {
      webgl: {
        powerPreference: 'high-performance',
        antialias: this.options.xrEnabled, // antialias is set here for XR only as it does not support postprocessing
      },
    };
  }

  public async start(): Promise<void> {
    console.log('[GameLoop] Starting...');

    if (!this.headless) {
      const defaultOptions = this.getDefaultRendererOptions();
      const rendererOptions: RendererOptions = {
        ...defaultOptions,
        ...this.options.rendererOptions,
        webgl: { ...defaultOptions.webgl, ...this.options.rendererOptions?.webgl },
        webgpu: { ...defaultOptions.webgpu, ...this.options.rendererOptions?.webgpu },
      };
      this.renderer = await Renderer.create(this.container, rendererOptions);
      resourceManager.initWithNativeRenderer(this.renderer.native);
      if (!isPublishedGame()) {
        await this.createDebugUI(this.renderer, false);
      }
    }

    await this.startWorldLifecycle();

    if (!this.world) {
      throw new Error('[GameLoop] World not initialized after start. This should not happen.');
    }

    // Auto-create VR button if XR is enabled
    if (this.options.xrEnabled) {
      this.createVRButton().catch(error => {
        console.warn('[GameLoop] failed to create VR button:', error);
      });
    }

    await this.enterPlayMode({ runPreStart: true });

    // Notify subclasses that the server is ready (dedicated server only, after beginPlay).
    if (NetRuntime.isDedicatedServer()) {
      const session = this.netWorld.getGameSession();
      if (session instanceof ServerGameSession) {
        this.onServerReady(session.getPort());
      }
    }

    // Local function to be used as animation loop callback
    let frameNumber = 0;
    const tickCallback = (currentTimeMS: number) => {
      if (this.destroyed) {
        return;
      }

      frameNumber += 1;
      if (frameNumber === 1) {
        THREE.ProfilerService.begin(`frame ${frameNumber - 1}`);
      } else {
        THREE.ProfilerService.end(`frame ${frameNumber - 1}`);
        THREE.ProfilerService.begin(`frame ${frameNumber}`);
      }

      if (!this.options.xrEnabled) {
        // For XR, this is handled by three.js itself using WebGlRenderer.setAnimationLoop
        requestAnimationFrame(tickCallback);
      }

      this.debugUI?.beginStats();
      // Initialize lastTime on first frame to prevent large initial deltaTime
      if (!this.lastTickTimeMS) {
        this.lastTickTimeMS = currentTimeMS;
        this.debugUI?.endStats();
        return;
      }

      const deltaTimeMS = currentTimeMS - this.lastTickTimeMS;
      this.lastTickTimeMS = currentTimeMS;

      this.tick({ currentTimeMS, deltaTimeMS });

      this.debugUI?.endStats();
    };

    // Always use WebGL renderer's setAnimationLoop when XR is enabled
    // This is required by Three.js for proper VR support
    const isHeadless = this.options.headless ?? NetRuntime.isDedicatedServer();
    if (this.options.xrEnabled && this.renderer) {
      this.renderer!.setupXRAnimationLoop(tickCallback);
    } else if (!isHeadless) {
      requestAnimationFrame(tickCallback);
    }

    await resourceManager.waitForResources(ResourceType.All);
    console.log('[GameLoop] resources loaded');
    console.log('[GameLoop] calling postStart...');
    this.postStart();
  }

  public destroy(): void {
    this.currentLevel = null;

    this.disposeDebugUI();

    if (this._world) {
      this.detachWorldScene(this._world);
      this._world.destroy();
    }

    this._world = null;
    this.connectionStatusOverlay?.hide();
    this.connectionStatusOverlay = null;
    this.netWorld.shutdown();
    this.disposeManifold();
    this.disposePhysicsEngine();
    this.disposeNavigationServer();
    this.disposePostProcessManager();

    this.renderer?.destroy();
    this.renderer = null;

    // Clean up VR button if it exists
    if (this.vrButton) {
      this.vrButton.remove();
      this.vrButton = null;
    }

    this.destroyed = true;
  }

  public setInputEnabled(enabled: boolean): void {
    this.world.inputManager.setInputEnabled(enabled);
  }

  /**
   * Reloads actor data into the **current** world without destroying it.
   * For map travel / scene file changes, use {@link openLevel} or {@link loadMap} instead.
   */
  public async loadLevelData(levelData: any): Promise<Level> {
    const isConnectedToServer = !!this.world.netWorld?.getGameSession();

    if (isConnectedToServer) {
      // When connected to a server, remove ALL actors except dynamically spawned ones from server
      // Dynamic actors (like PlayerController, PlayerInfo) are spawned at runtime by the server
      // Static actors (from scene file) should be removed and reloaded from the new map
      const allActors = [...this.world.getActors()];

      // Preserve only dynamic actors (not static objects from scene files)
      // Dynamic actors have remoteNetClientId set, static actors don't
      const actorsToPreserve = allActors.filter(actor => !actor.isStaticNetObject());
      const actorsToRemove = allActors.filter(actor => actor.isStaticNetObject());

      console.log(`[GameLoop] Connected to server - removing ${actorsToRemove.length} static actors, preserving ${actorsToPreserve.length} dynamic actor(s)`);
      this.world.removeActors(...actorsToRemove);

      // Clear the level reference since we removed everything from it
      this.currentLevel = null;
    } else {
      // In standalone mode, just unload the previous level normally
      if (this.currentLevel) {
        console.log(`[GameLoop] unloading previous level with ${this.currentLevel.actors.length} actors`);
        this.currentLevel.unload();
      }
    }

    // Server-side map transition: destroy the current GameMode before loading the new scene
    // so the incoming scene can declare its own GameMode class.
    // Save activePlayers so they can be transferred to the new GameSessionInfo afterwards.
    let savedActivePlayers: Map<string, import('../actors/PlayerInfo.js').PlayerInfo> | null = null;
    if (NetRuntime.isDedicatedServer() && this.world.gameMode) {
      const oldSessionInfo = this.world.gameMode.getGameSessionInfo();
      if (oldSessionInfo) {
        savedActivePlayers = new Map(oldSessionInfo.activePlayers);
        this.world.removeActors(oldSessionInfo);
      }
      if (this.world.gameMode.getPlayState() === PlayState.Playing) {
        this.world.gameMode.endPlay();
      }
      this.world.removeActors(this.world.gameMode);
      (this.world as any).gameMode = null;
      (this.world as any).gameModeClass = null; // let the scene set the new class
      console.log('[GameLoop] Destroyed old GameMode for map transition');
    }

    this.currentLevel = await WorldSerializer.loadWorld(this.world, levelData);

    // Server-side: create a fresh GameMode from the class declared by the new scene,
    // then transfer the preserved activePlayers into the new GameSessionInfo.
    if (NetRuntime.isDedicatedServer() && this.world.gameModeClass) {
      const newGameMode = spawn(this.world.gameModeClass);
      (this.world as any).gameMode = newGameMode;
      this.world.addActors(newGameMode); // triggers beginPlay → spawnGameSessionInfo

      if (savedActivePlayers && savedActivePlayers.size > 0) {
        const newSessionInfo = newGameMode.getGameSessionInfo();
        if (newSessionInfo) {
          for (const [clientId, playerInfo] of savedActivePlayers) {
            newSessionInfo.activePlayers.set(clientId, playerInfo);
          }
          console.log(`[GameLoop] Transferred ${savedActivePlayers.size} preserved PlayerInfo entries to new GameSessionInfo`);
        }
      }
    }

    return this.currentLevel;
  }

  /**
   * Replaces the active level (cold world load + {@link enterPlayMode}).
   * Uses {@link ISceneTransition} when not headless. This is the canonical map-transition API.
   */
  public async openLevel(scenePath: ScenePath, options: OpenLevelOptions = {}): Promise<void> {
    if (this._levelTransitionInProgress) {
      console.warn('[GameLoop] Ignoring openLevel — level transition already in progress');
      return;
    }

    const preserveActors = options.preserveActors ?? true;
    const targetPath = AssetPath.fromString(scenePath);
    const scenePathLabel = targetPath.asStringPath;
    console.log(`[GameLoop] Opening level from ${scenePath}`);

    this.onBeforeMapLoad(scenePathLabel);

    const preservedActors = preserveActors
      ? this.collectActorsToPreserveAcrossLevelTransition()
      : [];
    this.detachPreservedActorsAcrossLevelTransition(preservedActors);

    if (this.headless) {
      const level = await this.coldLoadLevelFromPath(targetPath);
      this.reinsertPreservedActorsAcrossLevelTransition(preservedActors);
      await this.enterPlayMode();
      this.onAfterMapLoad(scenePathLabel);
      await this.finalizeLevelOpen(level);
      console.log(`[GameLoop] ✓ Level opened from ${scenePath}`);
      return;
    }

    this._levelTransitionInProgress = true;
    const transition = this.getSceneTransition(targetPath);
    const ctx: TransitionContext = {
      gameLoop: this,
      container: this.container,
      targetScenePath: targetPath,
    };

    try {
      await transition.onBegin?.(ctx);
      this._activeSceneTransition = { transition, ctx };
      transition.update(ctx, 0);

      await this.waitUntilNextFrames(() => transition.shouldSwitchScenes(ctx));

      await this.destroyCurrentWorld();

      const level = await this.loadLevelFromPath(targetPath);

      this.reinsertPreservedActorsAcrossLevelTransition(preservedActors);

      await transition.onSceneLoaded?.(ctx);
      await this.enterPlayMode();

      await this.waitUntilNextFrames(() => transition.isFinished(ctx));

      this.onAfterMapLoad(scenePathLabel);
      await this.finalizeLevelOpen(level);
      console.log(`[GameLoop] ✓ Level opened from ${scenePath}`);
    } finally {
      transition.dispose?.();
      this._activeSceneTransition = null;
      this._levelTransitionInProgress = false;
      this._transitionFrameWaiter = null;
    }
  }

  /**
   * Loads a map by short name (e.g. `my-level` → `@project/my-level.genesys-scene`) or full scene path.
   * @deprecated Use {@link openLevel} or {@link open} instead.
   */
  public async loadMap(mapPath: string): Promise<void> {
    console.warn('[GameLoop] loadMap() is deprecated — use openLevel() or open() instead.');
    await this.openLevel(this.resolveMapArgToScenePath(mapPath));
  }

  /**
   * @deprecated Use {@link openLevel} or {@link loadMap} instead.
   */
  public async loadLevelDataFromPath(path: AssetPath): Promise<Level> {
    console.warn('[GameLoop] loadLevelDataFromPath() is deprecated — use openLevel() or loadMap() instead.');
    await this.openLevel(path.asStringPath);
    if (!this.currentLevel) {
      throw new Error(`[GameLoop] openLevel did not produce a level for ${path}`);
    }
    return this.currentLevel;
  }

  /**
   * Loads scene resources (NavMesh) based on scene metadata.
   */
  protected async loadSceneResources(world: World): Promise<void> {
    const resources = world.sceneResources;
    if (!resources) {
      return;
    }

    if (resources.navMesh) {
      await this.loadNavMeshFromPath(resources.navMesh.path);
    }
  }

  /**
   * Applies co-located lightmap build-data for the active scene path.
   */
  protected async loadSceneLightmaps(world: World): Promise<void> {
    const scenePath = this.activeScenePath;
    if (!scenePath) {
      return;
    }
    await tryApplyLightmapsAfterSceneReady(world, scenePath.asStringPath);
  }

  /**
   * Loads NavMesh from an explicit file path.
   */
  protected async loadNavMeshFromPath(navMeshPath: string): Promise<void> {
    if (!this.world) {
      return;
    }
    try {
      const assetPath = new AssetPath(navMeshPath);
      const provider = projectInfo.storageProvider;

      // Load NavMesh data directly
      const navMeshBuffer = await provider.downloadFileAsBuffer(assetPath);
      const navigationServer = this.navigationServer;

      if (navigationServer) {
        const success = await navigationServer.importNavigationMeshFromData(navMeshBuffer);
        if (success) {
          console.log(`[GameLoop] ✓ NavMesh loaded from: ${navMeshPath}`);
          return;
        }
      }

    } catch (error) {
      console.warn(`[GameLoop] ⚠️ Could not load NavMesh from: ${navMeshPath}`, error);
    }

    // Fallback: Generate NavMesh from scene geometry if auto-generation is enabled
    const navigationOptions = this.world.options.navigationOptions;
    if (navigationOptions.generateOnStartUp) {
      try {
        console.log('[GameLoop] 🔄 Falling back to auto-generation of NavMesh');
        await this.world.createNavigationMeshFromScene();
        console.log('[GameLoop] ✓ NavMesh auto-generated from scene geometry');
      } catch (error) {
        console.error('[GameLoop] ✗ Failed to auto-generate NavMesh', error);
      }
    } else {
      console.log('[GameLoop] ℹ️ NavMesh auto-generation disabled');
    }
  }

  public getWorld(): World | null {
    return this._world;
  }

  public getLevel(): Level | null {
    return this.currentLevel;
  }

  public enableXR(): void {
    this.options.xrEnabled = true;
    this.renderer?.enableXR();
  }

  public disableXR(): void {
    this.options.xrEnabled = false;
    this.renderer?.disableXR();
  }

  public async createVRButton(): Promise<HTMLElement | null> {
    try {
      if (!this.renderer) {
        throw new Error('Cannot create VR button in headless mode');
      }

      // Clean up existing VR button if any
      if (this.vrButton) {
        this.vrButton.remove();
        this.vrButton = null;
      }

      // Use utility function with configurable features
      // VR requires WebGL renderer (WebGPU XR is experimental)
      const webglRenderer = this.renderer.native.asWebGL();
      if (webglRenderer) {
        this.vrButton = await createVRButton(
          webglRenderer,
          this.container,
          this.options.vrSessionOptions ?? {}
        );
        return this.vrButton;
      }
      return null;
    } catch (error) {
      console.error('[GameLoop] failed to load VRButton:', error);
      return Promise.reject(error);
    }
  }

  public isXREnabled(): boolean {
    return this.renderer?.isXREnabled() ?? false;
  }

  public isXRPresenting(): boolean {
    return this.renderer?.isXRPresenting() ?? false;
  }

  public async exportNavigationMesh(scenePath: AssetPath): Promise<{ success: boolean; data?: ArrayBuffer; filename?: string }> {
    if (!this.world) {
      return { success: false };
    }
    return await this.world.exportNavigationMesh(scenePath);
  }

  public async importNavigationMesh(navMeshData?: ArrayBuffer): Promise<boolean> {
    if (!this.world) {
      return false;
    }
    if (navMeshData) {
      // Import from provided data
      const navigationServer = this.navigationServer;
      if (navigationServer) {
        return await navigationServer.importNavigationMeshFromData(navMeshData);
      }
      return false;
    }

    // Import from scene resources configuration
    const navMeshPath = this.world.sceneResources?.navMesh?.path;
    if (navMeshPath) {
      await this.loadNavMeshFromPath(navMeshPath);
      return true;
    }

    console.error('[GameLoop] importNavigationMesh: No NavMesh data provided and no navMesh configured in scene resources');
    return false;
  }

  //====================================================================================================

  /**
   * Called during {@link enterPlayMode} on first boot only ({@link start}), after scene
   * resources are loaded but before navigation setup and beginPlay. Override to configure
   * GameMode options, add static actors, or set session configuration.
   */
  protected async preStart(): Promise<void> {
  }

  /**
   * Called after the render loop is registered and world.beginPlay() has completed.
   * Override to spawn player controllers, start timers, or hook UI.
   */
  protected postStart(): void {
  }

  /**
   * Called after the WebSocket server has bound to a port, before any clients connect.
   */
  protected onServerReady(_port: number): void {
    // Override in subclasses to react to the server binding successfully.
  }

  /**
   * Dispatches to {@link connectToServer} or {@link openLevel} based on arg.
   * Server URLs are detected by `ws://` or `wss://` prefix; other strings are scene paths or map names.
   */
  public async open(arg: string): Promise<void> {
    if (arg.startsWith('ws://') || arg.startsWith('wss://')) {
      await this.connectToServer(arg);
    } else {
      await this.openLevel(this.resolveMapArgToScenePath(arg));
    }
  }

  /**
   * Called by {@link openLevel} before destroying the current world. Override to show a loading screen.
   */
  protected onBeforeMapLoad(_scenePath: string): void {
  }

  /**
   * Called by {@link openLevel} after {@link enterPlayMode} and before {@link finalizeLevelOpen}.
   * Override to hide a loading screen.
   */
  protected onAfterMapLoad(_scenePath: string): void {
  }

  /**
   * Actors to carry across {@link openLevel} without destroying them.
   * @remarks
   * TODO: Replace hard-coded `PlayerController` preservation with a configurable policy
   * (e.g. tag-based or game-mode-driven preserve list).
   */
  protected collectActorsToPreserveAcrossLevelTransition(): Actor[] {
    if (!this._world) {
      return [];
    }
    const controllers = this._world.getActors(PlayerController);
    // When connected as a client, only carry the server-owned autonomous proxy — not a stale
    // standalone Authority controller left over from offline play before connect.
    if (this.netWorld instanceof ClientNetWorld && this.netWorld.getGameSession()) {
      return controllers.filter(
        (controller) => controller.isLocalAutonomousProxy() && controller.isOwnedByLocalClient(),
      );
    }
    return controllers;
  }

  protected detachPreservedActorsAcrossLevelTransition(preservedActors: Actor[]): void {
    if (this._world && preservedActors.length > 0) {
      this._world.detachActorsPreservingAcrossLevelTransition(...preservedActors);
    }
  }

  protected reinsertPreservedActorsAcrossLevelTransition(preservedActors: Actor[]): void {
    if (this._world && preservedActors.length > 0) {
      this._world.addActors(...preservedActors);
    }
  }

  /**
   * Scene transition used by {@link openLevel}. Override in game subclasses for custom effects.
   */
  protected getSceneTransition(_targetScenePath: AssetPath): ISceneTransition {
    return new FadeToBlackTransition();
  }

  @THREE.profile
  protected tick(tickTime: TickTime): void {
    const dt = tickTime.deltaTimeMS / 1000;

    if (this._activeSceneTransition) {
      const { ctx, transition } = this._activeSceneTransition;
      transition.update(ctx, dt);
      this.resolveTransitionFrameWaiter();
    }

    if (this._world) {
      this._world.tick(dt);
    }

    void this.netWorld.tick(dt);

    if (this.renderer) {
      THREE.ProfilerService.begin('GenesysRenderer.render');
      this.renderer.native.beginRenderFrame();
      this.renderFrame(this.renderer.native, this.renderer.getWindowSize());
      this.renderer.native.endRenderFrame();
      THREE.ProfilerService.end('GenesysRenderer.render');
    }
  }

  @THREE.profile
  protected renderFrame(renderer: IGenesysRenderer, windowSize: RenderWindowSize): void {
    const world = this._world;
    const camera = world?.getActiveCamera();
    if (world) {
      if (!camera) {
        world.showCameraWarning();
      } else {
        world.hideCameraWarning();
      }
      world.renderWindowSize = windowSize;
    }

    const cameraToUse = camera ?? world?.fallbackCamera ?? this._transitionFallbackCamera;

    const aspect = windowSize.width / windowSize.height;
    if (cameraToUse instanceof THREE.PerspectiveCamera) {
      cameraToUse.aspect = aspect;
      cameraToUse.updateProjectionMatrix();
    } else if (cameraToUse instanceof THREE.OrthographicCamera) {
      cameraToUse.left = cameraToUse.bottom * aspect;
      cameraToUse.right = cameraToUse.top * aspect;
      cameraToUse.updateProjectionMatrix();
    }

    // the three.js render function may be called multiple times on the same scene per frame
    // and we don't want to call scene.updateMatrixWorld() multiple times so we only do it once here
    const rootScene = this._rootScene;
    THREE.ProfilerService.begin('update scene');
    rootScene.matrixWorldAutoUpdate = false;
    rootScene.updateMatrixWorld();
    THREE.ProfilerService.end('update scene');

    Stats.updateRenderStats(renderer);

    try {
      SceneComponent.isRenderingScene = true;
      const postProcessManager = this.postProcessManager;
      if (world && postProcessManager) {
        postProcessManager.renderScene(renderer, rootScene, cameraToUse, windowSize);
      } else {
        renderer.render(rootScene, cameraToUse);
      }
    } finally {
      SceneComponent.isRenderingScene = false;
    }
  }

  /** Captures and downloads a compact performance snapshot JSON. */
  public capturePerformanceSnapshot(waitSeconds = 0): void {
    void this.capturePerformanceSnapshotAsync(waitSeconds);
  }

  private async capturePerformanceSnapshotAsync(waitSeconds: number): Promise<void> {
    if (!this.world) {
      console.log('[PerformanceSnapshot] No world loaded.');
      return;
    }

    const wasEnabled = THREE.ProfilerService.isEnabled();
    if (waitSeconds > 0 && !wasEnabled) {
      THREE.ProfilerService.setProfile('stats');
      THREE.ProfilerService.enable();
    }

    if (waitSeconds > 0) {
      await new Promise(resolve => setTimeout(resolve, waitSeconds * 1000));
    }

    const snapshot = collectPerformanceSnapshot(this.world, {
      gameId: this.options.gameId,
      fps: this.estimateFps(),
    });
    downloadPerformanceSnapshot(snapshot);
    console.log(formatPerformanceSnapshotSummary(snapshot));

    if (waitSeconds > 0 && !wasEnabled) {
      THREE.ProfilerService.disable();
    }
  }

  private estimateFps(): number | undefined {
    if (!THREE.ProfilerService.isEnabled()) {
      return undefined;
    }
    const stats = THREE.ProfilerService.getStats('BaseGameLoop.tick')
      ?? THREE.ProfilerService.getStats('World.tick');
    if (!stats || stats.avg <= 0) {
      return undefined;
    }
    return Math.round(1000 / stats.avg);
  }

  @ConsoleCommand('perf.snapshot', 'Download performance snapshot JSON (usage: perf.snapshot [waitSeconds])')
  protected perfSnapshot(waitSecondsStr?: string): string {
    if (!this.world) {
      return 'No world loaded.';
    }
    const waitSeconds = waitSecondsStr ? Math.max(0, parseFloat(waitSecondsStr)) : 0;
    this.capturePerformanceSnapshot(waitSeconds);
    return waitSeconds > 0
      ? `Capturing performance snapshot in ${waitSeconds}s...`
      : 'Downloading performance snapshot...';
  }

  @ConsoleCommand('profiler.enable', 'Enable CPU profiling (stats mode) — use profiler.report or perf.snapshot')
  protected profilerEnable(): boolean {
    THREE.ProfilerService.setProfile('stats');
    THREE.ProfilerService.enable();
    return true;
  }

  @ConsoleCommand('profiler.trace', 'Enable full trace profiling for Speedscope export')
  protected profilerTraceEnable(): boolean {
    THREE.ProfilerService.setProfile('full');
    THREE.ProfilerService.enable();
    return true;
  }

  @ConsoleCommand('profiler.disable', 'Disable CPU profiling')
  protected profilerDisable(): boolean {
    THREE.ProfilerService.disable();
    return true;
  }

  @ConsoleCommand('profiler.report', 'Print CPU timing table sorted by average cost')
  protected profilerReport(): boolean {
    THREE.ProfilerService.report();
    return true;
  }

  @ConsoleCommand('profiler.reset', 'Clear all accumulated profiling samples')
  protected profilerReset(): boolean {
    THREE.ProfilerService.reset();
    return true;
  }

  @ConsoleCommand('loadworld', 'Load world from file path (usage: loadworld <path>)')
  protected loadWorld(path: string): boolean {
    void this.openLevel(this.resolveMapArgToScenePath(path));
    return true;
  }

  @ConsoleCommand('reload', 'Reload the current world')
  protected reload(): boolean {
    if (!this.world) return false;
    const worldData = this.world.asExportedObject();
    const worldDataString = JSON.stringify(worldData);
    this.world.clearWorld();
    WorldSerializer.loadWorld(this.world, parseJson(worldDataString)).then(() => {
      // this is a hack as currently there's no way to restore the camera
      const controller = this.world?.getFirstActor(PlayerController);
      const pawn = this.world?.getFirstActor(Pawn);
      if (controller && pawn) {
        controller.possess(pawn);
      }

      console.dir(worldData);
    });

    return true;
  }

  protected onConnectionStarted(): void {
    this.connectionStatusOverlay ??= new ConnectionStatusOverlay(this.container);
    this.connectionStatusOverlay.show();
    this.connectionStatusOverlay.setStatus('connecting');
  }

  protected onConnectionEstablished(clientId: string): void {
    this.connectionStatusOverlay?.setStatus('connected', clientId);
  }

  protected onConnectionFailed(_error: Error): void {
    this.connectionStatusOverlay?.setStatus('disconnected');
  }

  protected onConnectionClosed(): void {
    this.connectionStatusOverlay?.hide();
  }

  public async connectToServer(serverUrl: string): Promise<void> {
    if (!this.netWorld) {
      throw new Error('[GameLoop] Cannot connect to server: netWorld not initialized');
    }

    // Check if already connected to avoid duplicate connection attempts.
    if (this.netWorld instanceof ClientNetWorld && this.netWorld.getGameSession() !== null) {
      console.log('[GameLoop] Already connected to server, skipping connection');
      return;
    }

    // Swap StandaloneNetWorld → ClientNetWorld for the duration of this server connection.
    if (this.netWorld instanceof StandaloneNetWorld) {
      console.log('[GameLoop] Upgrading StandaloneNetWorld → ClientNetWorld');
      const clientNetWorld = new ClientNetWorld(this.options.gameId ?? 'default');
      this.netWorld = clientNetWorld;
      if (this.world) {
        (this.world as any).netWorld = clientNetWorld;
        clientNetWorld.setWorld(this.world);
        // Actors registered with the previous StandaloneNetWorld; re-queue on the client net world.
        if (this.world.isPlaying()) {
          for (const actor of [...this.world.getActors()]) {
            clientNetWorld.addNetObject(actor);
          }
        }
      }
    }

    this.onConnectionStarted();

    try {
      console.log(`[GameLoop] Connecting to server: ${serverUrl}`);
      if (!(this.netWorld instanceof ClientNetWorld)) {
        throw new Error('[GameLoop] connectToServer requires a ClientNetWorld instance');
      }
      await this.netWorld.connectToServer({
        serverUrl,
        lagSimulationConfig: this.options.netSettings?.clientLagSimulationConfig,
      });

      NetRuntime.setType(NetRuntimeType.Client);
      const clientId = this.netWorld.getGameSession()?.getAssignedClientId();
      console.log(`[GameLoop] ✓ Connected to server with clientId: ${clientId}`);
      this.onConnectionEstablished(clientId ?? '');
    } catch (error) {
      console.error('[GameLoop] ✗ Failed to connect to server:', error);
      this.onConnectionFailed(error as Error);
      throw error;
    }
  }

  public async disconnectFromServer(): Promise<void> {
    if (!this.isConnectedToServer()) {
      console.log('[GameLoop] Not connected to server');
      return;
    }

    try {
      console.log('[GameLoop] Disconnecting from server...');

      // Phase 2: Fully shut down the client net world (session, net objects, heartbeat).
      if (this.netWorld instanceof ClientNetWorld) {
        this.netWorld.shutdown();
      } else if (this.netWorld) {
        this.netWorld.getGameSession()?.destroy();
        this.netWorld.clearNetObjects();
      }

      // Phase 3: Switch runtime type and install a fresh StandaloneNetWorld BEFORE the
      // world transition so that getWorldConfiguration() returns the correct netWorld
      // when GameContext creates the new world.
      NetRuntime.setType(NetRuntimeType.Standalone);
      this.netWorld = new StandaloneNetWorld(this.options.gameId ?? 'default');
      console.log('[GameLoop] ✓ Disconnected — transitioning to standalone world');

      const scenePath = this.getConfiguredInitialScenePath();
      if (!scenePath) {
        console.warn('[GameLoop] No scene path configured for post-disconnect reload');
      } else {
        await this.openLevel(scenePath.asStringPath, { preserveActors: false });
      }

      this.onConnectionClosed();
    } catch (error) {
      console.error('[GameLoop] Error during disconnect:', error);
      throw error;
    }
  }

  @ConsoleCommand('toggleconnectionstatus', 'Toggle connection status overlay')
  protected toggleConnectionStatus(): boolean {
    this.connectionStatusOverlay ??= new ConnectionStatusOverlay(this.container);
    this.connectionStatusOverlay.toggle();
    return true;
  }

  protected isConnectedToServer(): boolean {
    return this.world.netWorld?.getGameSession() !== null;
  }

  protected async createDebugUI(renderer: Renderer, disablePerfCounter: boolean = false): Promise<void> {
    if (this._debugUI) {
      return;
    }

    this._debugUI = new GameLoopDebugUI(this.container, {
      renderer,
      debugUIMode: this.options.debugUIMode,
      inspectorTogglePosition: this.options.inspectorTogglePosition,
      inspectorToggleOffset: this.options.inspectorToggleOffset,
      inspectorToggleScale: this.options.inspectorToggleScale,
      onDownloadPerformanceSnapshot: () => this.capturePerformanceSnapshot(),
      disablePerfCounter,
    });
    await this._debugUI.init();
  }

  protected disposeDebugUI(): void {
    this._debugUI?.dispose();
    this._debugUI = null;
  }
}
